import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Euro, MapPin, Users, Radio, CheckCircle2, ArrowRight } from "lucide-react";

export default function BecomeAgent() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", department: "", city: "", motivation: "" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/agent-applications", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSaving(false); }
  };

  return (
    <div className="fade-in has-bottom-nav" data-testid="become-agent-page">
      {/* Hero */}
      <div className="bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14 lg:py-20">
          <p className="label text-[color:var(--color-mint)] flex items-center gap-2 mb-4"><Handshake size={16}/> Programme Agents FRIPRI</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-5">
            DEVIENS L'AGENT FRIPRI<br/>DE TON DÉPARTEMENT.
          </h1>
          <p className="text-white/80 max-w-2xl text-base md:text-lg mb-8">
            Accompagne les vendeurs de ton secteur, développe ton réseau et gagne <strong className="text-[color:var(--color-mint)]">3% sur chacune de leurs ventes</strong>. Un seul agent par département — prends ta place.
          </p>
          <a href="#candidature" className="btn btn-mint" data-testid="hero-apply-btn">Je candidate <ArrowRight size={16}/></a>
        </div>
      </div>

      {/* Avantages */}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <h2 className="text-lg font-black tracking-tight mb-6">Ce que tu gagnes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          <Benefit icon={<Euro size={22} strokeWidth={2.5}/>} color="bg-[color:var(--color-mint)]" title="3% sur chaque vente" text="De tous tes filleuls, versés automatiquement sur ton compte via Stripe. À vie, tant qu'ils vendent."/>
          <Benefit icon={<MapPin size={22} strokeWidth={2.5}/>} color="bg-[color:var(--color-yellow)]" title="Ton territoire exclusif" text="Un seul agent FRIPRI par département. Ton code de parrainage unique, ton réseau, ton secteur."/>
          <Benefit icon={<Users size={22} strokeWidth={2.5}/>} color="bg-white" title="Un vrai rôle de terrain" text="Aide les vendeurs novices ou pressés : photos, annonces, expéditions. Tu es le visage de FRIPRI localement."/>
          <Benefit icon={<Radio size={22} strokeWidth={2.5}/>} color="bg-[color:var(--color-coral)] text-white" title="Accès Brocante Live" text="Anime tes propres ventes en direct : enchères flash et déstockage devant ta communauté."/>
        </div>

        {/* Comment ça marche */}
        <h2 className="text-lg font-black tracking-tight mb-6">Comment ça marche</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          {[
            ["1", "Tu candidates", "Remplis le formulaire ci-dessous. Notre équipe étudie ta candidature sous quelques jours."],
            ["2", "Tu reçois ton code", "Une fois validé, ton espace agent s'active avec ton code de parrainage unique (ex : FR75-A1B2)."],
            ["3", "Tu développes ton réseau", "Chaque vendeur inscrit avec ton code devient ton filleul : tu touches 3% sur toutes ses ventes."],
          ].map(([n, t, d]) => (
            <div key={n} className="border-2 border-black p-5 bg-white">
              <div className="w-10 h-10 bg-black text-white font-black text-xl flex items-center justify-center mb-3">{n}</div>
              <p className="font-black tracking-tight mb-1">{t}</p>
              <p className="text-sm text-[color:var(--color-text-muted)]">{d}</p>
            </div>
          ))}
        </div>

        {/* Formulaire */}
        <div id="candidature" className="max-w-2xl">
          <h2 className="text-2xl lg:text-3xl font-black tracking-tighter mb-6">Candidature</h2>
          {sent ? (
            <div className="border-2 border-black bg-[color:var(--color-mint)] p-8 text-center" data-testid="application-success">
              <CheckCircle2 size={40} className="mx-auto mb-3"/>
              <p className="text-2xl font-black tracking-tight mb-2">Candidature envoyée !</p>
              <p className="text-sm mb-4">Notre équipe l'examine et te répond par email très rapidement. Vérifie ta boîte mail (et tes spams).</p>
              <Link to="/catalogue" className="btn btn-primary btn-sm">Découvrir Fripri →</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="border-2 border-black bg-white p-5 lg:p-6 space-y-4" data-testid="agent-application-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label font-bold mb-2 block">Nom complet *</label>
                  <input className="field" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="apply-name"/>
                </div>
                <div>
                  <label className="label font-bold mb-2 block">Email *</label>
                  <input className="field" type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="apply-email"/>
                </div>
                <div>
                  <label className="label font-bold mb-2 block">Téléphone</label>
                  <input className="field" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="06 12 34 56 78" data-testid="apply-phone"/>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label font-bold mb-2 block">Département *</label>
                    <input className="field" required maxLength={3} value={form.department} onChange={e=>setForm({...form, department:e.target.value})} placeholder="75" data-testid="apply-department"/>
                  </div>
                  <div>
                    <label className="label font-bold mb-2 block">Ville</label>
                    <input className="field" value={form.city} onChange={e=>setForm({...form, city:e.target.value})} data-testid="apply-city"/>
                  </div>
                </div>
              </div>
              <div>
                <label className="label font-bold mb-2 block">Pourquoi toi ? *</label>
                <textarea className="field" rows={4} required minLength={20} value={form.motivation} onChange={e=>setForm({...form, motivation:e.target.value})} placeholder="Ton expérience de la seconde main, ton réseau local, ta motivation… (20 caractères min.)" data-testid="apply-motivation"/>
              </div>
              <button type="submit" disabled={saving} className="btn btn-primary w-full" data-testid="apply-submit-btn">
                {saving ? "Envoi…" : "Envoyer ma candidature →"}
              </button>
              <p className="text-xs text-[color:var(--color-text-muted)]">En candidatant, tu acceptes d'être contacté par l'équipe FRIPRI. Données traitées conformément à notre <Link to="/confidentialite" className="underline">politique de confidentialité</Link>.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon, title, text, color }) {
  return (
    <div className={`border-2 border-black p-5 ${color}`}>
      <div className="mb-3">{icon}</div>
      <p className="font-black tracking-tight mb-1">{title}</p>
      <p className="text-sm opacity-80">{text}</p>
    </div>
  );
}
